import React, { useCallback, useState, ReactElement } from 'react';
import Card from '../../../../components/card/card.component';
import Button from '../../../../components/button/button.component';
import ModalButtonBlock from '../../../../components/modal-button-block/modal-button-block.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import FieldUpload from '../../../../components/field-upload/field-upload.component';
import Loader from '../../../../components/loader/loader.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import ModalFieldBlock from '../../../../components/modal-field-block/modal-field-block.component';
import ModalDescription from '../../../../components/modal-description/modal-description.component';
import MainService from '../../../../services/main-service';
import SPREADSHEET_NAME_DEFAULT from '../../../../constants/system/spreadsheet-names';

const FileUpdateModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isSaving,
    updateFileFormatList,
    onCloseModal,
    onConfirm,
  } = props;

  const [updateFile, setUpdateFile]: [File | null, Function] = useState(null);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);

  const spreadsheetNamePlatform: string | undefined = MainService.PLATFORM_TITLE;

  const closeModal: Function = useCallback((): void => {
    setUpdateFile(null);
    setErrorMessage(null);

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const onUpdateFileDelete: Function = useCallback((): void => {
    setUpdateFile(null);
    setErrorMessage(null);
  }, []);

  const onUpdateFileChange: Function = useCallback((file: File): void => {
    setUpdateFile(file);
    setErrorMessage(null);
  }, []);

  const onUpdateFileError: Function = useCallback((error: string): void => {
    setErrorMessage(error);
  }, []);

  const onSuccess: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    setUpdateFile(null);
    setErrorMessage(null);
  }, []);

  return (
    <ModalLayout isOpen={isOpen} title="Update From File" onCloseModal={closeModal}>
      <Card isContainer>
        <ModalFieldBlock>
          <FieldUpload
            isPlain
            isDroppable
            uploadFile={updateFile}
            buttonLabel="Upload File"
            acceptedExtensionList={updateFileFormatList && updateFileFormatList.length
              ? updateFileFormatList.map((formatItem: FormatItem): string => formatItem.name) : ''}
            onFileChange={onUpdateFileChange}
            onFileDelete={onUpdateFileDelete}
            onError={onUpdateFileError}
          />
        </ModalFieldBlock>
        {
          updateFileFormatList && !!updateFileFormatList.length && (
            <ModalDescription>
              {
                `Accepted formats: `
                + `${updateFileFormatList.map((formatItem: FormatItem): string => formatItem.label)
                  .join(', ')}.`
              }
            </ModalDescription>
          )
        }
        <ModalDescription>
          Spreadsheet file must include a worksheet with
          {spreadsheetNamePlatform ? ` a name "${spreadsheetNamePlatform}" ` : ' '}
          or default name "{SPREADSHEET_NAME_DEFAULT}".
        </ModalDescription>
      </Card>
      <ModalButtonBlock>
        <Button
          isDisabled={!updateFile}
          onClick={(): void => {
            onConfirm(updateFile, onSuccess, onUpdateFileError);
          }}
        >
          Save
        </Button>
        {
          isSaving && (
            <Loader isInline isModal isSmall />
          )
        }
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
    </ModalLayout>
  );
});

export default FileUpdateModal;
